import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
  Modal,
  ActivityIndicator,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { ToastContainer, toast } from 'react-toastify';
import TopGreenHeader from './screens/Common/TopGreenHeader';
import API_PATHS from './apiCalls/ApiPath';

const FamilyScreen = ({ navigation }) => {
  const [loading, setLoading] = useState(true);
  const [isFamilyHead, setIsFamilyHead] = useState(false);
  const [headInfo, setHeadInfo] = useState(null);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);

  // ── Fetch family data every time screen opens ──────────────────────────────
  // Reuses the same FETCH_FAMILY endpoint as ManageFamilyScreen, since both
  // screens need to know: who is the head, and is the logged-in user the head.
  useFocusEffect(
    useCallback(() => {
      fetchFamilyData();
    }, [])
  );

  const fetchFamilyData = async () => {
    try {
      setLoading(true);
      const userId = await AsyncStorage.getItem('userId');
      const token = await AsyncStorage.getItem('token');

      const response = await fetch(
        `${API_PATHS.BASE_URL}${API_PATHS.FETCH_FAMILY}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            parentId: userId,
          }),
        }
      );
      const data = await response.json();

      if (data.Success === 1) {
        const familyHeadId = data.Data?.familyHeadId;
        const list = data.Data?.familyMembersList || [];

        setIsFamilyHead(userId === familyHeadId);

        // Find the head's own details inside the members list (name, etc.)
        // so we can display who the admin actually is on screen.
        const headMember = list.find((m) => m.userId === familyHeadId);
        setHeadInfo(headMember || null);
      } else {
        toast.error(data.Message || 'Failed to fetch family data');
      }
    } catch (error) {
      console.error('fetchFamilyData error:', error);
      toast.error('Something went wrong while fetching family data');
    } finally {
      setLoading(false);
    }
  };

  // ── Leave Family (members only, not head) ──────────────────────────────────
  const handleLeaveFamily = () => {
    setConfirmModal(true);
  };

  const performLeave = async () => {
    setConfirmModal(false);

    try {
      setLeaveLoading(true);
      const userId = await AsyncStorage.getItem('userId');
      const token = await AsyncStorage.getItem('token');

      const response = await fetch(
        `${API_PATHS.BASE_URL}${API_PATHS.LEAVE_FAMILY}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ parentId: userId }),
        }
      );

      const result = await response.json();

      if (result.Success === 1) {
        toast.success(result.Message || 'You have left the family');
        navigation.goBack();
      } else {
        toast.error(result.Message || 'Failed to leave family');
      }
    } catch (error) {
      console.error('handleLeaveFamily error:', error);
      toast.error('Something went wrong while leaving family');
    } finally {
      setLeaveLoading(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <TopGreenHeader Heading="Family" isBackIcon={true} isNotification={false} navigation={navigation} />
        <ActivityIndicator size="large" color="#8BC34A" style={{ marginTop: 40 }} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <TopGreenHeader
        Heading="Family"
        isBackIcon={true}
        isNotification={false}
        navigation={navigation}
      />

      <ScrollView contentContainerStyle={styles.content}>

        {/* ── Status Card ── */}
        <View style={styles.card}>
          <Text style={styles.cardLabel}>ACCOUNT TYPE</Text>
          <Text style={styles.statusHeading}>
            Current Status:{'\n'}{isFamilyHead ? 'Head' : 'Member'}
          </Text>

          <View style={styles.divider} />

          <Text style={styles.cardLabel}>Family Head</Text>

          {/* ── Admin Row ── */}
          <View style={styles.adminRow}>
            {/* Avatar */}

            <View style={styles.adminAvatar}>
              <View style={styles.initialsBox}>
                <Text style={styles.initialsText}>
                  {headInfo?.name?.charAt(0)?.toUpperCase() || '?'}
                </Text>
              </View>
            </View>

            {/* Admin Info */}
            <View>
              <Text style={styles.adminName}>{headInfo?.name || 'Unknown'}</Text>
              <Text style={styles.adminJoined}>{headInfo?.email || ''}</Text>
            </View>
          </View>
        </View>

        {/* ── Manage Family Card ── */}
        <TouchableOpacity
          style={styles.manageCard}
          onPress={() => navigation.navigate('ManageFamily')}
        >
          <View>
            <Text style={styles.manageTitle}>Manage Family</Text>
            <Text style={styles.manageDesc}>
              {isFamilyHead ? 'Invite or remove family members' : 'View family members'}
            </Text>
          </View>
          <View style={styles.manageArrow} />
        </TouchableOpacity>

        {/* ── Danger Zone Card — only visible to members, NOT the head ── */}
        {!isFamilyHead && (
          <View style={styles.dangerCard}>
            <Text style={styles.dangerTitle}>Danger Zone</Text>
            <Text style={styles.dangerDesc}>
              Leaving this family will remove your access to shared bookings, calendars, and secure messages permanently.
            </Text>

            <TouchableOpacity
              style={styles.leaveButton}
              onPress={handleLeaveFamily}
              disabled={leaveLoading}
            >
              {leaveLoading ? (
                <ActivityIndicator color="#c0392b" />
              ) : (
                <>
                  {/* Leave icon (arrow out of box) */}
                  <View style={styles.leaveIconWrapper}>
                    <View style={styles.leaveIconBox} />
                    <View style={styles.leaveIconArrow} />
                  </View>
                  <Text style={styles.leaveButtonText}>LEAVE FAMILY</Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        )}

      </ScrollView>

      <Modal
        animationType="slide"
        transparent={true}
        visible={confirmModal}
        onRequestClose={() => setConfirmModal(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalText}>
              Leaving this family will remove your access to shared bookings. Are you sure?
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={styles.modalButton} onPress={performLeave}>
                <Text style={styles.modalButtonText}>Yes</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalButton} onPress={() => setConfirmModal(false)}>
                <Text style={styles.modalButtonText}>No</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <ToastContainer position="top-center" autoClose={3000} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F0F2F5',
  },
  content: {
    padding: 16,
    paddingBottom: 40,
  },

  // ── Status Card ──
  card: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#e0e8d0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  cardLabel: {
    fontSize: 11,
    color: '#888',
    letterSpacing: 1.2,
    fontWeight: '600',
    marginBottom: 8,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  statusHeading: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#111',
    lineHeight: 36,
    marginBottom: 20,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  activeBadge: {
    alignSelf: 'flex-start',
    backgroundColor: '#f0f8e8',
    borderRadius: 12,
    paddingHorizontal: 24,
    paddingVertical: 10,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#d4edaa',
  },
  activeBadgeText: {
    fontSize: 16,
    color: '#4a7c1b',
    fontWeight: '500',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  divider: {
    height: 1,
    backgroundColor: '#eee',
    marginBottom: 16,
  },
  adminRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  adminAvatar: {
    width: 64,
    height: 64,
    borderRadius: 10,
    overflow: 'hidden',
    marginRight: 14,
    backgroundColor: '#c6e39e',
  },
  initialsBox: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#b8d98a',
  },
  initialsText: {
    fontSize: 26,
    fontWeight: 'bold',
    color: '#3b6d11',
  },
  adminName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111',
    marginBottom: 4,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  adminJoined: {
    fontSize: 14,
    color: '#888',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },

  // ── Manage Family Card ──
  manageCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#e0e8d0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  manageTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111',
    marginBottom: 4,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  manageDesc: {
    fontSize: 13,
    color: '#888',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  manageArrow: {
    width: 10,
    height: 10,
    borderTopWidth: 2,
    borderRightWidth: 2,
    borderColor: '#8BC34A',
    transform: [{ rotate: '45deg' }],
  },

  // ── Danger Zone Card ──
  dangerCard: {
    backgroundColor: '#fff8f8',
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: '#f5c6c6',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 3,
    elevation: 1,
  },
  dangerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#c0392b',
    marginBottom: 10,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  dangerDesc: {
    fontSize: 14,
    color: '#555',
    lineHeight: 21,
    marginBottom: 20,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  leaveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: '#c0392b',
    borderRadius: 10,
    paddingVertical: 16,
    gap: 10,
  },
  leaveIconWrapper: {
    width: 20,
    height: 18,
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
  },
  leaveIconBox: {
    width: 14,
    height: 14,
    borderWidth: 2,
    borderColor: '#c0392b',
    borderRadius: 2,
    position: 'absolute',
    left: 0,
  },
  leaveIconArrow: {
    width: 10,
    height: 2,
    backgroundColor: '#c0392b',
    position: 'absolute',
    right: 0,
    top: 8,
  },
  leaveButtonText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#c0392b',
    letterSpacing: 1,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },

  // ── Confirm Modal ──
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalContent: {
    width: 300,
    padding: 20,
    backgroundColor: '#fff',
    borderRadius: 10,
    alignItems: 'center',
  },
  modalText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
    color: '#333',
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  modalButton: {
    flex: 1,
    padding: 10,
    marginHorizontal: 10,
    backgroundColor: '#8BC34A',
    alignItems: 'center',
    borderRadius: 5,
  },
  modalButtonText: {
    color: '#fff',
    fontSize: 16,
  },
});

export default FamilyScreen;